import { useState, useEffect } from 'react';
import { useFirebase } from '../Auth/FirebaseProvider';
import { statsService } from '../../services/dashboardService';
import {
  Box,
  Typography,
  IconButton,
  Paper
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  TrendingUp
} from 'lucide-react';
import { FaMoneyBillWave, FaShoppingCart } from 'react-icons/fa';
import { formatCurrency } from '../../utils/helpers';

const RevenueCalendar = () => {
  const { foodTruckId, loading: contextLoading } = useFirebase();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dailyRevenue, setDailyRevenue] = useState({});
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!foodTruckId || contextLoading) {
      setService(null);
      return;
    }

    try {
      const statsServiceInstance = statsService.getInstance(foodTruckId);
      setService(statsServiceInstance);
    } catch (error) {
      console.error("Error initializing stats service:", error);
      setService(null);
    }
  }, [foodTruckId, contextLoading]);

  useEffect(() => {
    if (!service) return;

    setLoading(true);
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 1);

    const unsubscribe = service.listenDailyOrderStats(
      startDate,
      endDate,
      (dailyStats) => {
        const revenue = {};
        Object.entries(dailyStats).forEach(([dateKey, stats]) => {
          // dateKey est au format YYYY-MM-DD, on le garde tel quel
          revenue[dateKey] = {
            total: stats.revenue || 0,
            orders: stats.count || 0
          };
        });
        setDailyRevenue(revenue);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribe && typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [service, currentDate]);

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    // Obtenir le premier jour de la semaine (0 = dimanche, 1 = lundi, etc.)
    // Ajuster pour que lundi soit 0
    const startDay = (firstDay.getDay() + 6) % 7;
    
    // Créer un tableau avec les jours vides au début + les jours du mois
    const calendarDays = [];
    
    // Ajouter les jours vides du début
    for (let i = 0; i < startDay; i++) {
      calendarDays.push(null);
    }
    
    // Ajouter les jours du mois avec leurs informations
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      calendarDays.push({
        day,
        date,
        dayName: date.toLocaleDateString('fr-FR', { weekday: 'long' })
      });
    }
    
    return calendarDays;
  };

  const getDayRevenue = (day) => {
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // +1 car getMonth() retourne 0-11
    const dayStr = String(day).padStart(2, '0');
    const dateKey = `${year}-${month}-${dayStr}`;
    return dailyRevenue[dateKey] || { total: 0, orders: 0 };
  };

  // Calculer la moyenne des revenus par jour de la semaine
  const getWeekdayAverage = (targetDayOfWeek) => {
    const revenues = Object.entries(dailyRevenue)
      .filter(([dateKey, data]) => {
        const date = new Date(dateKey);
        return date.getDay() === targetDayOfWeek && data.total > 0;
      })
      .map(([, data]) => data.total);
    
    if (revenues.length === 0) return 0;
    return revenues.reduce((sum, revenue) => sum + revenue, 0) / revenues.length;
  };

  // Obtenir les couleurs en fonction de la performance
  const getPerformanceColors = (revenue, dayOfWeek) => {
    if (revenue <= 0) return {
      bg: '#fef3c7',
      border: '#f59e0b',
      text: '#92400e'
    };

    const average = getWeekdayAverage(dayOfWeek);
    if (average === 0) return {
      bg: '#fef3c7',
      border: '#f59e0b', 
      text: '#92400e'
    };

    const ratio = revenue / average;
    
    if (ratio >= 1.2) {
      // Excellente performance (20%+ au-dessus)
      return {
        bg: '#d1fae5',
        border: '#10b981',
        text: '#064e3b'
      };
    } else if (ratio >= 1.05) {
      // Bonne performance (5-20% au-dessus)
      return {
        bg: '#ecfdf5',
        border: '#22c55e',
        text: '#14532d'
      };
    } else if (ratio >= 0.95) {
      // Performance normale (±5%)
      return {
        bg: '#fef3c7',
        border: '#f59e0b',
        text: '#92400e'
      };
    } else if (ratio >= 0.8) {
      // Performance en dessous (-5 à -20%)
      return {
        bg: '#fed7aa',
        border: '#ea580c',
        text: '#9a3412'
      };
    } else {
      // Performance faible (-20%+)
      return {
        bg: '#fecaca',
        border: '#ef4444',
        text: '#991b1b'
      };
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getFullYear() === currentDate.getFullYear() &&
           today.getMonth() === currentDate.getMonth() &&
           today.getDate() === day;
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  const days = getDaysInMonth();
  const totalMonthRevenue = Object.values(dailyRevenue).reduce((sum, day) => sum + day.total, 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            bgcolor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <TrendingUp size={20} color="white" />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Chiffre d'affaires par jour
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total du mois : {formatCurrency(totalMonthRevenue)}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigateMonth(-1)} size="small">
            <ChevronLeft size={20} />
          </IconButton>
          <Typography variant="h6" sx={{ minWidth: 160, textAlign: 'center', fontWeight: 600 }}>
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </Typography>
          <IconButton onClick={() => navigateMonth(1)} size="small">
            <ChevronRight size={20} />
          </IconButton>
        </Box>
      </Box>

      {/* En-têtes des jours de la semaine */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2,
        mb: 2
      }}>
        {dayNames.map((dayName) => (
          <Box
            key={dayName}
            sx={{
              p: 1,
              textAlign: 'center',
              bgcolor: 'primary.main',
              borderRadius: 1,
              color: 'white'
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                fontSize: '0.8rem'
              }}
            >
              {dayName}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Grille des jours */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2
      }}>
        {days.map((dayInfo, index) => {
          if (!dayInfo) {
            // Jour vide
            return <Box key={`empty-${index}`} sx={{ minHeight: 100 }} />;
          }

          const { day, date } = dayInfo;
          const revenue = getDayRevenue(day);
          const hasRevenue = revenue.total > 0;
          const dayOfWeek = date.getDay();
          const performanceColors = getPerformanceColors(revenue.total, dayOfWeek);
          
          return (
            <Paper
              key={day}
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: isToday(day) 
                  ? 'primary.main' 
                  : hasRevenue 
                    ? 'success.main' 
                    : 'divider',
                borderRadius: 3,
                minHeight: 100,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all 0.2s ease-in-out',
                bgcolor: isToday(day) ? 'primary.50' : 'background.paper',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': hasRevenue ? {
                  boxShadow: '0 6px 20px 0 rgb(0 0 0 / 0.15)',
                  transform: 'translateY(-3px)',
                  borderColor: 'success.dark'
                } : {},
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: isToday(day) ? 700 : 600,
                    color: isToday(day) ? 'primary.main' : 'text.primary',
                    fontSize: '1.2rem'
                  }}
                >
                  {day}
                </Typography>
                {hasRevenue && (
                  <Box sx={{
                    border: '1px solid #10b981',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                  }}>
                    <FaShoppingCart size={10} style={{ color: '#10b981' }} />
                    <Box sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: '#10b981',
                      color: 'white',
                      borderRadius: '50%',
                      width: 18,
                      height: 18,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      border: '2px solid white'
                    }}>
                      {revenue.orders}
                    </Box>
                  </Box>
                )}
              </Box>
              
              {hasRevenue && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 'auto' }}>
                  {/* Badge CA avec indicateur de performance */}
                  <Box sx={{
                    border: `1px solid ${performanceColors.border}`,
                    borderRadius: 3,
                    px: 1,
                    py: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 'fit-content'
                  }}>
                    <FaMoneyBillWave size={10} style={{ color: performanceColors.border }} />
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 700,
                        color: performanceColors.text,
                        fontSize: '0.65rem',
                        lineHeight: 1
                      }}
                    >
                      {formatCurrency(revenue.total)}
                    </Typography>
                  </Box>
                </Box>
              )}
              
              {!hasRevenue && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.disabled',
                    textAlign: 'center',
                    fontStyle: 'italic'
                  }}
                >
                  Aucune vente
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Chargement des données...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default RevenueCalendar;